import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DeleteOutline,
  Link as LinkIcon,
  PauseCircleOutline,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  IProcessManager,
  IProcessManagerLandRegister,
} from '../../Interfaces/IProcessManager';
import { ILandParcelEntry } from '../../Interfaces/IRealEstate';
import { ICourtAdminstrativeUnitAssignment } from '../../Interfaces/ICourtAdminstrativeUnitAssignment';
import { useBulkTrackingAction } from '../../hooks/reactQuerys/ProcessManagerQueries';
import { useAlert } from '../../context/AlertContext';

interface IProps {
  title: string;
  processManagerArray: IProcessManagerLandRegister[];
  realEstateLandParcelEntries: ILandParcelEntry[];
  adminUnitsAssign: ICourtAdminstrativeUnitAssignment[];
  onChanged: () => void;
}

const stateDot = (pm: IProcessManager): React.ReactElement => {
  const today = new Date();
  const dateToCheck = new Date(String(pm.DateToCheck));
  const diffInDays =
    (today.getTime() - dateToCheck.getTime()) / (1000 * 60 * 60 * 24);
  let color = '#4caf50';
  if (pm.idProcessManagerState !== 1) color = '#eeeeee';
  else if (diffInDays > 14) color = '#f44336';
  else if (diffInDays > 7) color = '#ffeb3b';
  return (
    <Tooltip
      title={
        pm.idProcessManagerState !== 1
          ? `Abgeschlossen am ${new Date(String(pm.Closed_at)).toLocaleString()}`
          : `Prüfung seit ${Math.floor(diffInDays)} Tagen fällig`
      }
    >
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          backgroundColor: color,
          border: '1px solid rgba(0,0,0,0.1)',
          display: 'inline-block',
        }}
      />
    </Tooltip>
  );
};

/**
 * Flache Nachverfolgungs-Tabelle mit Mehrfachauswahl + Bulk-Aktionen
 * (Deaktivieren = schließen, Löschen = Soft-Delete). Zeigt BEIDE Verknüpfungs-
 * arten: die alte (``data.idRealEstateLandRegisterEntry``) und die neue,
 * blatt-zentrische (``data.idGrundbuchblatt``) — im Gegensatz zur früheren
 * nach Gericht gruppierten Tabelle, die blatt-zentrische Trackings ausließ.
 */
export const ProcessManagerTrackingBulkTable: React.FC<IProps> = ({
  title,
  processManagerArray,
  realEstateLandParcelEntries,
  adminUnitsAssign,
  onChanged,
}) => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { bulkTrackingAction, isBulkTrackingActionPending } =
    useBulkTrackingAction();
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Nur offene Nachverfolgungen sind auswählbar (geschlossene/gelöschte nicht).
  const selectableIds = useMemo(
    () =>
      processManagerArray
        .filter(pm => pm.idProcessManagerState === 1)
        .map(pm => pm.idProcessManager as number),
    [processManagerArray]
  );

  const toggle = (id: number) =>
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allSelected =
    selectableIds.length > 0 && selectableIds.every(id => selected.has(id));
  const someSelected = selectableIds.some(id => selected.has(id));

  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(selectableIds));

  const runBulk = async (action: 'deactivate' | 'delete') => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (
      action === 'delete' &&
      !window.confirm(
        `${ids.length} Nachverfolgung(en) wirklich löschen? Das kann nicht rückgängig gemacht werden.`
      )
    ) {
      return;
    }
    try {
      await bulkTrackingAction({ ids, action });
      showAlert({
        text:
          action === 'delete'
            ? `${ids.length} Nachverfolgung(en) gelöscht.`
            : `${ids.length} Nachverfolgung(en) deaktiviert.`,
        severity: 'success',
      });
      setSelected(new Set());
      onChanged();
    } catch (err) {
      showAlert({
        text:
          err instanceof Error
            ? `Aktion fehlgeschlagen: ${err.message}`
            : 'Aktion fehlgeschlagen.',
        severity: 'error',
      });
    }
  };

  const gegenstand = (pm: IProcessManagerLandRegister): string => {
    const idBlatt = (pm.data as { idGrundbuchblatt?: number }).idGrundbuchblatt;
    const idEntry = pm.data.idRealEstateLandRegisterEntry;
    const entry = realEstateLandParcelEntries.find(
      e => e.idRealEstateLandRegisterEntry === idEntry
    );
    if (entry?.SheetLandRegister) return `Blatt ${entry.SheetLandRegister}`;
    if (idBlatt) return `Grundbuchblatt #${idBlatt}`;
    if (idEntry) return `Flurstück #${idEntry}`;
    return '–';
  };

  const gemarkung = (pm: IProcessManagerLandRegister): string => {
    const entry = realEstateLandParcelEntries.find(
      e =>
        e.idRealEstateLandRegisterEntry ===
        pm.data.idRealEstateLandRegisterEntry
    );
    return (
      adminUnitsAssign.find(
        a => a.idAdministrativeUnit === entry?.idAdministrativeUnit
      )?.AdministrativeUnit ?? '–'
    );
  };

  return (
    <Paper variant='outlined' sx={{ mb: 3, p: 1 }}>
      <Stack
        direction='row'
        alignItems='center'
        spacing={1}
        sx={{ px: 1, py: 0.5, minHeight: 48 }}
      >
        <Typography variant='h6' sx={{ flexGrow: 1 }}>
          {title}
        </Typography>
        {selected.size > 0 && (
          <>
            <Typography variant='body2' color='text.secondary'>
              {selected.size} ausgewählt
            </Typography>
            <Button
              size='small'
              startIcon={<PauseCircleOutline />}
              disabled={isBulkTrackingActionPending}
              onClick={() => runBulk('deactivate')}
            >
              Deaktivieren
            </Button>
            <Button
              size='small'
              color='error'
              startIcon={<DeleteOutline />}
              disabled={isBulkTrackingActionPending}
              onClick={() => runBulk('delete')}
            >
              Löschen
            </Button>
          </>
        )}
      </Stack>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell padding='checkbox'>
              <Checkbox
                size='small'
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                disabled={selectableIds.length === 0}
                onChange={toggleAll}
              />
            </TableCell>
            <TableCell width={10}>Status</TableCell>
            <TableCell>Art</TableCell>
            <TableCell>Gegenstand</TableCell>
            <TableCell>Gemarkung</TableCell>
            <TableCell align='right'>Prüfungen</TableCell>
            <TableCell>Prüfungsdatum</TableCell>
            <TableCell>Letzte Prüfung</TableCell>
            <TableCell width={50}>Aktionen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {processManagerArray.map(pm => {
            const id = pm.idProcessManager as number;
            const closed = pm.idProcessManagerState !== 1;
            return (
              <TableRow
                key={`pm-track-${id}`}
                hover
                selected={selected.has(id)}
              >
                <TableCell padding='checkbox'>
                  <Checkbox
                    size='small'
                    checked={selected.has(id)}
                    disabled={closed}
                    onChange={() => toggle(id)}
                  />
                </TableCell>
                <TableCell>{stateDot(pm)}</TableCell>
                <TableCell>{pm.ProcessManagerType}</TableCell>
                <TableCell>{gegenstand(pm)}</TableCell>
                <TableCell>{gemarkung(pm)}</TableCell>
                <TableCell align='right'>{pm.Tries}</TableCell>
                <TableCell>
                  {pm.DateToCheck
                    ? new Date(pm.DateToCheck).toLocaleDateString()
                    : '–'}
                </TableCell>
                <TableCell>
                  {pm.LastTry_at
                    ? new Date(pm.LastTry_at).toLocaleString()
                    : '–'}
                </TableCell>
                <TableCell>
                  <Tooltip title='Zum Vorgang'>
                    <span>
                      <Button
                        size='small'
                        onClick={() =>
                          navigate(`/legaltransaction/${pm.idLegalTransaction}`)
                        }
                      >
                        <LinkIcon fontSize='small' />
                      </Button>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
};
